import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import classNames from 'classnames'
import TitleRow from '../../../components/title-row'
import { Button } from '../../../components/UI'
import FormField from './FormField'
import styles from '../styles.module.scss'

const FormSection = ({
  section,
  formFields,
  errors,
  onChange,
  onSelectChange,
  optionsMap,
  userData,
  collapsible = false,
  defaultExpanded = true,
  forceExpanded = false,
  onExpansionChange,
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  useEffect(() => {
    if (forceExpanded && !isExpanded) {
      setIsExpanded(true)

      if (onExpansionChange) {
        onExpansionChange(true)
      }
    }
  }, [forceExpanded, isExpanded, onExpansionChange])

  const renderFields = () => {
    if (!section.fields || !Array.isArray(section.fields)) {
      return null
    }

    return section.fields.map(field => {
      const fieldOptions = field.options ? optionsMap[field.options] || [] : []
      const fieldValue = formFields[field.name]
      const fieldError = errors[field.validation] || errors[field.name] || ''

      return (
        <FormField
          key={field.id}
          field={field}
          value={fieldValue}
          error={fieldError}
          onChange={onChange}
          onSelectChange={onSelectChange}
          options={fieldOptions}
          userData={userData}
          {...props}
        />
      )
    })
  }

  const renderSpecialSection = () => {
    if (section.component === 'Button') {
      const padding =
        section.title === 'password.reset.change' ? '15px 50px' : '15px 28px'

      const buttonProps = {
        ...section.componentProps,
        label: <FormattedMessage id={section.componentProps.label} />,
        action: props[section.componentProps.action],
      }

      return (
        <div style={{ padding }}>
          <Button {...buttonProps} />
        </div>
      )
    }

    return null
  }

  if (!section) {
    return null
  }

  // Check if this is a special section with conditional rendering
  if (
    section.condition === '!userData.emailActivated' &&
    userData?.emailActivated
  ) {
    return null
  }

  const toggleExpanded = () => {
    if (collapsible) {
      const newExpandedState = !isExpanded
      setIsExpanded(newExpandedState)

      if (onExpansionChange) {
        onExpansionChange(newExpandedState)
      }
    }
  }

  return (
    <section className={classNames(styles.formSection, styles.slideInUp)}>
      {section.title && (
        <div
          className={classNames(styles.sectionHeader, {
            [styles.collapsible]: collapsible,
            [styles.expanded]: isExpanded,
          })}
          onClick={toggleExpanded}
        >
          <TitleRow
            className={classNames('settingsMainTitleBg')}
            title={<FormattedMessage id={section.title} />}
            color='white'
            boxshadow
            padding={section.titlePadding || '15px 20px'}
            margin={section.margin || '0'}
            fontSize={section.fontSize || 20}
          />

          {collapsible && (
            <div className={styles.collapseIcon}>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className={classNames({
                  [styles.rotated]: isExpanded,
                })}
              >
                <polyline points='6,9 12,15 18,9'></polyline>
              </svg>
            </div>
          )}
        </div>
      )}

      <div
        className={classNames(styles.formFieldsContainer, {
          [styles.collapsed]: collapsible && !isExpanded,
        })}
      >
        {section.fields ? renderFields() : renderSpecialSection()}
      </div>
    </section>
  )
}

export default FormSection
